import { Link } from 'react-router-dom';
import './ErrorState.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Стан помилки завантаження.
 *
 * Замінює нескінченний лоадер: раніше сторінка показувала індикатор, поки в
 * сховищі порожньо, а порожньо там лишалося назавжди, якщо запит упав. Тепер
 * порожній результат і збій запиту — два різні стани з різним виглядом.
 *
 * Текст добирається тут, а не в сховищі: там лежить лише вид помилки, бо мову
 * інтерфейсу користувач може перемкнути вже після збою.
 *
 * variant:
 *   section — усередині секції, коли решта сторінки лишається корисною
 *   page    — на всю сторінку, коли без цих даних показувати нічого
 */

const COPY = {
  network: {
    ua: {
      title: "Немає зв'язку із сервером",
      text: 'Перевірте підключення до інтернету. Якщо воно є, сервер може бути тимчасово недоступний.',
    },
    en: {
      title: 'No connection to the server',
      text: 'Check your internet connection. If it is fine, the server may be temporarily unavailable.',
    },
  },
  notFound: {
    ua: { title: 'Дані не знайдено', text: 'Схоже, цей запис видалили або адреса містить помилку.' },
    en: { title: 'Data not found', text: 'This record may have been deleted, or the address contains a mistake.' },
  },
  forbidden: {
    ua: { title: 'Доступ закрито', text: 'Цей розділ недоступний. Якщо це помилка, напишіть нам.' },
    en: { title: 'Access denied', text: 'This section is not available. If this is a mistake, please contact us.' },
  },
  server: {
    ua: { title: 'Сервер не відповідає', text: 'Це тимчасово. Спробуйте оновити сторінку за хвилину.' },
    en: { title: 'The server is not responding', text: 'This is temporary. Please try again in a minute.' },
  },
  client: {
    ua: { title: 'Не вдалося завантажити дані', text: 'Запит не пройшов. Спробуйте ще раз.' },
    en: { title: 'Could not load the data', text: 'The request did not go through. Please try again.' },
  },
  unknown: {
    ua: { title: 'Щось пішло не так', text: 'Не вдалося завантажити дані. Спробуйте ще раз.' },
    en: { title: 'Something went wrong', text: 'The data could not be loaded. Please try again.' },
  },
};

/**
 * @param {{ kind: string, status: number|null }} [error] вид помилки зі сховища
 * @param {string[]} [messages] готові рядки. Ними користуються каталоги e-phan API
 * @param {() => void} [onRetry] якщо передано, показується кнопка повтору
 */
export default function ErrorState({
  error,
  messages,
  onRetry,
  variant = 'section',
  className = '',
  children,
}) {
  const language = useLanguage();

  const kind = error?.kind && COPY[error.kind] ? error.kind : 'unknown';
  const { title, text } = COPY[kind][language];

  const list = (messages || []).filter(Boolean);

  // Порада «перезавантажте сторінку» користувачеві без мережі не допоможе:
  // запит однаково не пройде. Тому для цього виду пропонуємо саме повтор.
  const retryLabel = language === 'en' ? 'Try again' : 'Спробувати ще раз';

  return (
    <div className={`error-state error-state--${variant} ${className}`} role='alert'>
      <span className='error-state-mark' aria-hidden='true'>
        <ErrorGlyph kind={kind} />
      </span>

      <div className='error-state-body'>
        <h2 className='error-state-title'>{title}</h2>

        {list.length > 1 ? (
          <ul className='error-state-list'>
            {list.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : (
          <p className='error-state-text'>{children || list[0] || text}</p>
        )}

        <div className='error-state-actions'>
          {onRetry && (
            <button type='button' className='btn btn--primary' onClick={onRetry}>
              {retryLabel}
              <svg className='error-state-retry-icon' width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
                <path
                  d='M20 12a8 8 0 1 1-2.5-5.8M20 4.5V9h-4.5'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          )}

          {variant === 'page' && (
            <Link className='btn btn--secondary' to='/'>
              {language === 'en' ? 'Home' : 'На головну'}
            </Link>
          )}
        </div>

        {error?.status && (
          <p className='error-state-code tnum'>
            {language === 'en' ? 'Error code' : 'Код помилки'} {error.status}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Значки намальовані тут, а не взяті з бібліотеки: решта сайту так само
 * тримає свої контури інлайном із товщиною 1.6 і круглими кінцями, і додавати
 * заради трьох гліфів залежність із власною сіткою означало б завести на
 * сторінці друге сімейство піктограм.
 */
function ErrorGlyph({ kind }) {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  if (kind === 'network') {
    return (
      <svg width='24' height='24' viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M2.5 8.5a15 15 0 0 1 19 0M5.5 12a11 11 0 0 1 13 0M8.5 15.5a7 7 0 0 1 7 0M12 19h.01' {...stroke} />
        <path d='m3 3 18 18' {...stroke} />
      </svg>
    );
  }

  if (kind === 'notFound') {
    return (
      <svg width='24' height='24' viewBox='0 0 24 24' aria-hidden='true'>
        <circle cx='11' cy='11' r='6.5' {...stroke} />
        <path d='m15.8 15.8 4.2 4.2M9 11h4' {...stroke} />
      </svg>
    );
  }

  return (
    <svg width='24' height='24' viewBox='0 0 24 24' aria-hidden='true'>
      <circle cx='12' cy='12' r='8.5' {...stroke} />
      <path d='M12 7.5v5M12 16h.01' {...stroke} />
    </svg>
  );
}
